import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { getAuthSession, saveAuthSession, physioApi, getMediaUrl } from '../services/api';

const ProfileEditPage = () => {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    specialization: '',
    education: '',
    shortDescription: '',
    therapyFor: [],
    languages: ['English'],
    location: '',
    yearsOfExperience: 0,
    membership: 'free',
    bio: '',
    introVideoUrl: '',
    introVideoTitle: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadError, setVideoUploadError] = useState('');
  const [videoUploadSuccess, setVideoUploadSuccess] = useState('');
  const videoFileInputRef = useRef(null);

  const [customTherapy, setCustomTherapy] = useState('');
  const [customLanguage, setCustomLanguage] = useState('');

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const defaultTherapyOptions = [
    'Anxiety & Panic',
    'Depressive Disorders',
    'Stress Management',
    'Relationship & Marriage Issues',
    'Trauma & PTSD',
    'Grief & Bereavement',
    'Sleep & Insomnia',
    'OCD & Intrusive Thoughts',
    'Workplace Burnout & Career Stress',
    'Self-Esteem & Confidence',
    'Anger Management',
    'ADHD / Neurodiversity Support'
  ];

  const defaultLanguageOptions = [
    'English',
    'Hindi',
    'Marathi',
    'Bengali',
    'Kannada',
    'Tamil',
    'Telugu',
    'Gujarati',
    'Malayalam',
    'Punjabi',
    'Urdu'
  ];

  useEffect(() => {
    if (!authSession?.token) {
      navigate('/login');
      return;
    }

    const fetchProfile = async () => {
      try {
        setLoading(true);
        setErrorMessage('');
        const res = await physioApi.me();
        const user = res.user || authSession.user || {};
        const profile = res.profile || {};

        setFormData({
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          specialization: profile.specialization || user.specialization || '',
          education: profile.education || user.education || '',
          shortDescription: profile.shortDescription || user.shortDescription || '',
          therapyFor: (profile.therapyFor && profile.therapyFor.length > 0) ? profile.therapyFor : (user.therapyFor || []),
          languages: (profile.languages && profile.languages.length > 0) ? profile.languages : (user.languages || ['English', 'Hindi']),
          location: profile.location || user.location || '',
          yearsOfExperience: profile.yearsOfExperience !== undefined ? profile.yearsOfExperience : (user.yearsOfExperience || 0),
          membership: profile.membership || user.membership || 'free',
          bio: profile.bio || user.bio || '',
          introVideoUrl: profile.introVideoUrl || user.introVideoUrl || '',
          introVideoTitle: profile.introVideoTitle || user.introVideoTitle || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } catch (err) {
        console.error('Failed to load therapist profile:', err);
        setErrorMessage(err.message || 'Unable to load profile data.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [authSession?.token, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (statusMessage) setStatusMessage('');
  };

  const toggleTherapy = (item) => {
    setFormData((prev) => {
      const exists = prev.therapyFor.includes(item);
      return {
        ...prev,
        therapyFor: exists ? prev.therapyFor.filter((c) => c !== item) : [...prev.therapyFor, item],
      };
    });
  };

  const addCustomTherapy = (e) => {
    if (e) e.preventDefault();
    const val = customTherapy.trim();
    if (val && !formData.therapyFor.includes(val)) {
      setFormData((prev) => ({ ...prev, therapyFor: [...prev.therapyFor, val] }));
      setCustomTherapy('');
    }
  };

  const toggleLanguage = (lang) => {
    setFormData((prev) => {
      const exists = prev.languages.includes(lang);
      if (exists && prev.languages.length <= 1) return prev;
      return {
        ...prev,
        languages: exists ? prev.languages.filter((l) => l !== lang) : [...prev.languages, lang],
      };
    });
  };

  const addCustomLanguage = (e) => {
    if (e) e.preventDefault();
    const val = customLanguage.trim();
    if (val && !formData.languages.includes(val)) {
      setFormData((prev) => ({ ...prev, languages: [...prev.languages, val] }));
      setCustomLanguage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');

    if (formData.newPassword) {
      if (formData.newPassword.length < 6) {
        setErrorMessage('New password must be at least 6 characters long.');
        return;
      }
      if (formData.newPassword !== formData.confirmPassword) {
        setErrorMessage('New password and confirm password do not match.');
        return;
      }
      if (!formData.currentPassword) {
        setErrorMessage('Current password is required to change password.');
        return;
      }
    }

    setSaving(true);

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        specialization: formData.specialization.trim(),
        education: formData.education.trim(),
        shortDescription: formData.shortDescription.trim(),
        therapyFor: formData.therapyFor,
        languages: formData.languages,
        location: formData.location.trim(),
        yearsOfExperience: parseInt(formData.yearsOfExperience) || 0,
        bio: formData.bio.trim(),
        introVideoUrl: formData.introVideoUrl || '',
        introVideoTitle: formData.introVideoTitle || '',
      };

      if (formData.newPassword) {
        payload.currentPassword = formData.currentPassword;
        payload.newPassword = formData.newPassword;
      }

      const res = await physioApi.updateMyProfile(payload);

      if (res.user) {
        setFormData((prev) => ({
          ...prev,
          name: res.user.name || prev.name,
          email: res.user.email || prev.email,
          phone: res.user.phone !== undefined ? res.user.phone : prev.phone,
          specialization: res.user.specialization || prev.specialization,
          education: res.user.education || prev.education,
          shortDescription: res.user.shortDescription || prev.shortDescription,
          therapyFor: res.user.therapyFor || prev.therapyFor,
          languages: res.user.languages || prev.languages,
          location: res.user.location || prev.location,
          yearsOfExperience: res.user.yearsOfExperience !== undefined ? res.user.yearsOfExperience : prev.yearsOfExperience,
          bio: res.user.bio || prev.bio,
          introVideoUrl: res.user.introVideoUrl !== undefined ? res.user.introVideoUrl : prev.introVideoUrl,
          introVideoTitle: res.user.introVideoTitle !== undefined ? res.user.introVideoTitle : prev.introVideoTitle,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));

        if (authSession) {
          saveAuthSession({
            ...authSession,
            user: {
              ...authSession.user,
              ...res.user,
            },
          });
        }
      } else {
        setFormData((prev) => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
      }

      setStatusMessage('🎉 Profile and account details updated successfully!');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Failed to update therapist profile:', err);
      setErrorMessage(err.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const isPro = String(formData.membership || authSession?.membership || '').toLowerCase() === 'pro';

  const handleVideoFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isPro) {
      setVideoUploadError('💎 Video uploads are exclusively available for Pro Members. Please upgrade your membership to Pro.');
      if (videoFileInputRef.current) videoFileInputRef.current.value = '';
      return;
    }

    const validExts = /\.(mp4|webm|mov|m4v|mkv)$/i;
    if (!validExts.test(file.name) && !file.type.startsWith('video/')) {
      setVideoUploadError('Please select a valid video file (.mp4, .webm, .mov, .m4v).');
      return;
    }

    if (file.size > 200 * 1024 * 1024) {
      setVideoUploadError('Video file exceeds 200MB limit.');
      return;
    }

    try {
      setVideoUploading(true);
      setVideoUploadError('');
      setVideoUploadSuccess('');
      const uploadFormData = new FormData();
      uploadFormData.append('video', file);
      const res = await physioApi.uploadIntroVideo(uploadFormData);
      const fileUrl = res.url || res.fileUrl;
      const autoTitle = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');

      setFormData(prev => ({
        ...prev,
        introVideoUrl: fileUrl,
        introVideoTitle: prev.introVideoTitle || autoTitle,
      }));
      setVideoUploadSuccess('✓ Video uploaded successfully! Make sure to click "Save Profile Changes" below.');
    } catch (err) {
      console.error('Video upload error:', err);
      setVideoUploadError(err.message || 'Failed to upload video file. Please try again.');
    } finally {
      setVideoUploading(false);
      if (videoFileInputRef.current) videoFileInputRef.current.value = '';
    }
  };

  return (
    <>
      <DashboardSidebar userRole="physiotherapist" />
      <div
        style={{
          minHeight: '100vh',
          background: '#f8fafc',
          padding: '40px 20px',
          marginLeft: '280px',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <Link
            to="/physio-dashboard"
            style={{
              color: '#7c3aed',
              textDecoration: 'none',
              marginBottom: '20px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            ← Back to Therapist Dashboard
          </Link>

          <div style={{ marginBottom: '24px' }}>
            <h1 style={{ fontSize: '28px', color: '#1e293b', margin: '10px 0 6px 0', fontWeight: 700 }}>
              👤 Practitioner Profile & Account Settings
            </h1>
            <p style={{ color: '#64748b', margin: 0, fontSize: '14px' }}>
              Manage your personal credentials, contact info, professional bio, and account password
            </p>
          </div>

          {statusMessage && (
            <div
              style={{
                background: '#ecfdf5',
                color: '#065f46',
                border: '1px solid #a7f3d0',
                padding: '14px 18px',
                borderRadius: '10px',
                marginBottom: '20px',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {statusMessage}
            </div>
          )}

          {errorMessage && (
            <div
              style={{
                background: '#fef2f2',
                color: '#991b1b',
                border: '1px solid #fecaca',
                padding: '14px 18px',
                borderRadius: '10px',
                marginBottom: '20px',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              ⚠️ {errorMessage}
            </div>
          )}

          {loading ? (
            <div style={{ background: 'white', borderRadius: '16px', padding: '50px', textAlign: 'center' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }}></div>
              <p style={{ color: '#64748b', margin: 0 }}>Loading profile information...</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '32px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                border: '1px solid #e2e8f0',
              }}
            >
              {/* Account & Contact Information */}
              <div style={{ marginBottom: '30px', paddingBottom: '24px', borderBottom: '1px solid #f1f5f9' }}>
                <h3 style={{ fontSize: '17px', color: '#1e293b', marginBottom: '16px', fontWeight: 700 }}>
                  Personal & Contact Information
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="e.g. Dr. Rajesh Sharma"
                      style={{ width: '100%', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="doctor@example.com"
                      style={{ width: '100%', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="e.g. +91 98765 43210"
                      style={{ width: '100%', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                    <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                      Used for client WhatsApp communication and consultation links
                    </span>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                      Membership Tier
                    </label>
                    <input
                      type="text"
                      value={formData.membership === 'pro' ? '⭐ Pro Practitioner' : 'Free Practitioner'}
                      readOnly
                      style={{ width: '100%', padding: '12px 14px', border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', color: '#64748b', fontWeight: 600, cursor: 'not-allowed' }}
                    />
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div style={{ marginBottom: '30px', paddingBottom: '24px', borderBottom: '1px solid #f1f5f9' }}>
                <h3 style={{ fontSize: '17px', color: '#1e293b', marginBottom: '16px', fontWeight: 700 }}>
                  Professional Profile Information
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                      Specialization / Clinical Focus
                    </label>
                    <input
                      type="text"
                      name="specialization"
                      value={formData.specialization}
                      onChange={handleChange}
                      placeholder="e.g. Clinical Psychology, Cognitive Behavioral Therapy"
                      style={{ width: '100%', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                      🎓 Education & Qualifications
                    </label>
                    <input
                      type="text"
                      name="education"
                      value={formData.education}
                      onChange={handleChange}
                      placeholder="e.g. M.A. Clinical Psychology, M.Phil, RCI Licensed"
                      style={{ width: '100%', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                      Location (City, Clinic / State)
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="e.g. Mumbai, Maharashtra"
                      style={{ width: '100%', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                      ⏳ Years of Experience
                    </label>
                    <input
                      type="number"
                      min="0"
                      name="yearsOfExperience"
                      value={formData.yearsOfExperience}
                      onChange={handleChange}
                      style={{ width: '100%', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                    📝 Short Description / Profile Headline
                  </label>
                  <input
                    type="text"
                    name="shortDescription"
                    value={formData.shortDescription}
                    onChange={handleChange}
                    placeholder="e.g. Senior Clinical Psychologist specializing in Cognitive Behavioral Therapy & anxiety relief"
                    style={{ width: '100%', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                  <small style={{ color: '#64748b', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    A concise 1-2 sentence tagline shown on your practitioner card & search results.
                  </small>
                </div>

                {/* I OFFER THERAPY FOR */}
                <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ margin: 0, fontWeight: 700, color: '#581c87', fontSize: '13px' }}>
                      🧠 I Offer Therapy For ({formData.therapyFor.length} Selected)
                    </label>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                    {defaultTherapyOptions.map((item) => {
                      const isSelected = formData.therapyFor.includes(item);
                      return (
                        <button
                          key={item}
                          type="button"
                          onClick={() => toggleTherapy(item)}
                          style={{
                            padding: '4px 10px',
                            borderRadius: '16px',
                            fontSize: '11px',
                            fontWeight: isSelected ? 600 : 500,
                            border: isSelected ? '1px solid #7c3aed' : '1px solid #cbd5e1',
                            background: isSelected ? '#7c3aed' : '#fff',
                            color: isSelected ? '#fff' : '#475569',
                            cursor: 'pointer',
                          }}
                        >
                          {isSelected ? '✓ ' : '+ '} {item}
                        </button>
                      );
                    })}
                    {formData.therapyFor.filter((i) => !defaultTherapyOptions.includes(i)).map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => toggleTherapy(item)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '16px',
                          fontSize: '11px',
                          fontWeight: 600,
                          border: '1px solid #059669',
                          background: '#059669',
                          color: '#fff',
                          cursor: 'pointer',
                        }}
                      >
                        ✓ {item} ✕
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="Add custom concern / specialty..."
                      value={customTherapy}
                      onChange={(e) => setCustomTherapy(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCustomTherapy();
                        }
                      }}
                      style={{ flex: 1, padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }}
                    />
                    <button
                      type="button"
                      onClick={addCustomTherapy}
                      style={{ padding: '6px 12px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      + Add
                    </button>
                  </div>
                </div>

                {/* LANGUAGES */}
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ margin: 0, fontWeight: 700, color: '#0369a1', fontSize: '13px' }}>
                      🗣️ Languages Spoken ({formData.languages.length} Selected)
                    </label>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                    {defaultLanguageOptions.map((lang) => {
                      const isSelected = formData.languages.includes(lang);
                      return (
                        <button
                          key={lang}
                          type="button"
                          onClick={() => toggleLanguage(lang)}
                          style={{
                            padding: '4px 10px',
                            borderRadius: '16px',
                            fontSize: '11px',
                            fontWeight: isSelected ? 600 : 500,
                            border: isSelected ? '1px solid #0284c7' : '1px solid #cbd5e1',
                            background: isSelected ? '#0284c7' : '#fff',
                            color: isSelected ? '#fff' : '#475569',
                            cursor: 'pointer',
                          }}
                        >
                          {isSelected ? '✓ ' : '+ '} {lang}
                        </button>
                      );
                    })}
                    {formData.languages.filter((l) => !defaultLanguageOptions.includes(l)).map((lang) => (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => toggleLanguage(lang)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '16px',
                          fontSize: '11px',
                          fontWeight: 600,
                          border: '1px solid #059669',
                          background: '#059669',
                          color: '#fff',
                          cursor: 'pointer',
                        }}
                      >
                        ✓ {lang} ✕
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="Add another language..."
                      value={customLanguage}
                      onChange={(e) => setCustomLanguage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCustomLanguage();
                        }
                      }}
                      style={{ flex: 1, padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px' }}
                    />
                    <button
                      type="button"
                      onClick={addCustomLanguage}
                      style={{ padding: '6px 12px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      + Add
                    </button>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                    Professional Bio & Clinical Approach
                  </label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Share your therapeutic background, degrees, certifications, and how you help clients..."
                    style={{ width: '100%', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', lineHeight: 1.5 }}
                  />
                </div>
              </div>

              {/* 🎬 Video Introduction Section (Pro Only) */}
              <div style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '17px', color: '#1e293b', margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>🎬</span> Video Introduction
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 800,
                      letterSpacing: '0.6px',
                      textTransform: 'uppercase',
                      padding: '3px 8px',
                      borderRadius: '12px',
                      background: isPro ? 'linear-gradient(135deg, #7c3aed, #a855f7)' : '#e2e8f0',
                      color: isPro ? '#fff' : '#64748b'
                    }}>
                      {isPro ? '★ Pro Feature' : '🔒 Pro Only'}
                    </span>
                  </h3>
                  {!isPro && (
                    <Link
                      to="/pricing"
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#7c3aed',
                        textDecoration: 'none',
                        background: '#f3e8ff',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <span>✨</span> Upgrade to Pro
                    </Link>
                  )}
                </div>

                <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '13px' }}>
                  A personal introduction video helps prospective clients connect with your presence and clinical approach before booking. Pro members' videos are featured prominently on your public profile and search listings.
                </p>

                {isPro ? (
                  <div style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '20px'
                  }}>
                    {videoUploadError && (
                      <div style={{
                        padding: '10px 14px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#dc2626',
                        borderRadius: '8px',
                        fontSize: '13px',
                        marginBottom: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span>⚠️</span>
                        <span>{videoUploadError}</span>
                      </div>
                    )}

                    {videoUploadSuccess && (
                      <div style={{
                        padding: '10px 14px',
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        color: '#16a34a',
                        borderRadius: '8px',
                        fontSize: '13px',
                        marginBottom: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span>✓</span>
                        <span>{videoUploadSuccess}</span>
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: formData.introVideoUrl ? '1fr 1fr' : '1fr', gap: '20px' }}>
                      {/* Left: Uploader & Link input */}
                      <div>
                        {/* Hidden file input */}
                        <input
                          ref={videoFileInputRef}
                          type="file"
                          accept="video/mp4,video/webm,video/quicktime,video/x-m4v,video/mkv"
                          onChange={handleVideoFileUpload}
                          style={{ display: 'none' }}
                        />

                        <div
                          onClick={() => videoFileInputRef.current?.click()}
                          style={{
                            border: '2px dashed #a78bfa',
                            borderRadius: '10px',
                            background: '#ffffff',
                            padding: '24px 16px',
                            textAlign: 'center',
                            cursor: videoUploading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            marginBottom: '14px'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.background = '#faf5ff'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#a78bfa'; e.currentTarget.style.background = '#ffffff'; }}
                        >
                          <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                            {videoUploading ? '⏳' : '📤'}
                          </div>
                          <p style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>
                            {videoUploading ? 'Uploading Video to Secure Server...' : 'Click to Upload Video File (.mp4, .webm, .mov)'}
                          </p>
                          <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                            Recommended: 1-3 minute introduction under 200MB.
                          </p>
                        </div>

                        {/* Or Enter Video Link */}
                        <div style={{ marginBottom: '14px' }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                            🔗 Or Provide Video URL (YouTube, Vimeo, or Hosted MP4)
                          </label>
                          <input
                            type="text"
                            value={formData.introVideoUrl || ''}
                            onChange={(e) => setFormData({ ...formData, introVideoUrl: e.target.value })}
                            placeholder="e.g., https://www.youtube.com/watch?v=... or /uploads/video.mp4"
                            style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                          />
                        </div>

                        {/* Video Caption / Title */}
                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                            🏷️ Video Title / Short Caption
                          </label>
                          <input
                            type="text"
                            value={formData.introVideoTitle || ''}
                            onChange={(e) => setFormData({ ...formData, introVideoTitle: e.target.value })}
                            placeholder="e.g., Welcome message & clinical background"
                            style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>

                      {/* Right: Preview Player if video exists */}
                      {formData.introVideoUrl ? (
                        <div style={{ background: '#0f172a', borderRadius: '12px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px', color: '#fff' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#a78bfa' }}>
                              👁️ Live Video Preview
                            </span>
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, introVideoUrl: '', introVideoTitle: '' })}
                              style={{
                                background: '#dc2626',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '4px 10px',
                                fontSize: '11px',
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                            >
                              🗑️ Remove Video
                            </button>
                          </div>

                          <div style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', background: '#000', minHeight: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {formData.introVideoUrl.includes('youtube.com') || formData.introVideoUrl.includes('youtu.be') ? (
                              <iframe
                                src={formData.introVideoUrl.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')}
                                title="Video Preview"
                                style={{ width: '100%', height: '220px', border: 'none' }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            ) : formData.introVideoUrl.includes('vimeo.com') ? (
                              <iframe
                                src={formData.introVideoUrl.replace('vimeo.com/', 'player.vimeo.com/video/')}
                                title="Video Preview"
                                style={{ width: '100%', height: '220px', border: 'none' }}
                                allowFullScreen
                              />
                            ) : (
                              <video
                                src={getMediaUrl(formData.introVideoUrl)}
                                controls
                                playsInline
                                style={{ width: '100%', maxHeight: '220px', objectFit: 'contain' }}
                              />
                            )}
                          </div>

                          <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                            <strong>Title:</strong> {formData.introVideoTitle || 'Personal Introduction'}
                            <div style={{ marginTop: '4px', color: '#86efac', fontSize: '11px' }}>
                              ✓ Visible to clients on your frontend profile & directory listing
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div style={{
                    background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                    border: '1.5px dashed #c084fc',
                    borderRadius: '12px',
                    padding: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '20px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                      <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '10px',
                        background: '#7c3aed',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '22px',
                        flexShrink: 0
                      }}>
                        📹
                      </div>
                      <div>
                        <h4 style={{ margin: '0 0 6px 0', fontSize: '15px', fontWeight: 700, color: '#4c1d95' }}>
                          Unlock Video Introductions with Pro Membership
                        </h4>
                        <p style={{ margin: 0, fontSize: '13px', color: '#6b21a8', lineHeight: 1.5, maxWidth: '520px' }}>
                          Therapists with video introductions receive significantly more consultation bookings. Pro members can upload video intros visible directly to prospective clients on the frontend directory and detailed profile.
                        </p>
                      </div>
                    </div>
                    <Link
                      to="/pricing"
                      style={{
                        padding: '10px 18px',
                        background: '#7c3aed',
                        color: '#ffffff',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 700,
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <span>💎</span> Upgrade to Pro
                    </Link>
                  </div>
                )}
              </div>

              {/* Password & Security Section */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ fontSize: '17px', color: '#1e293b', marginBottom: '6px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🔒</span> Password & Security
                </h3>
                <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '13px' }}>
                  Leave these fields blank if you do not want to change your password.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                      Current Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        name="currentPassword"
                        value={formData.currentPassword}
                        onChange={handleChange}
                        placeholder="Current password"
                        style={{ width: '100%', padding: '12px 36px 12px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                      >
                        {showCurrentPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                      New Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleChange}
                        placeholder="Min. 6 characters"
                        style={{ width: '100%', padding: '12px 36px 12px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                      >
                        {showNewPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                      Confirm New Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Repeat new password"
                        style={{ width: '100%', padding: '12px 36px 12px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                      >
                        {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: '12px 32px',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
                  }}
                >
                  {saving ? 'Saving...' : '💾 Save Profile Changes'}
                </button>
                <Link
                  to="/physio-dashboard"
                  style={{
                    padding: '12px 24px',
                    background: '#f1f5f9',
                    color: '#475569',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '14px',
                    textDecoration: 'none',
                  }}
                >
                  Cancel
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ProfileEditPage;
